import { useEffect, useRef, useState } from "react"
import { LuLoaderCircle, LuX } from "react-icons/lu"
import { useHistory } from "react-router-dom"

import Button from "~/v2/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/v2/components/ui/dialog"

type HistoryTransition = {
  hash?: string
  pathname: string
  search?: string
}

type UnsavedChangesGuardProps = {
  hasUnsavedChanges?: boolean
  isSaving?: boolean
  onDiscard: () => void
  onSave: () => Promise<void> | void
}

const buildNextLocation = (tx: HistoryTransition) => ({
  hash: tx.hash,
  pathname: tx.pathname,
  search: tx.search,
})

const UnsavedChangesGuard = ({
  hasUnsavedChanges,
  isSaving = false,
  onDiscard,
  onSave,
}: UnsavedChangesGuardProps) => {
  const blockRef = useRef<(() => void) | null>(null)
  const pendingTransitionRef = useRef<HistoryTransition | null>(null)
  const history = useHistory()
  const [showBar, setShowBar] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    setShowBar(Boolean(hasUnsavedChanges))
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      blockRef.current?.()
      blockRef.current = null
      pendingTransitionRef.current = null
      setDialogOpen(false)
      return undefined
    }

    blockRef.current?.()
    blockRef.current = history.block((tx: HistoryTransition) => {
      pendingTransitionRef.current = tx
      setDialogOpen(true)
      return false
    })

    return () => {
      blockRef.current?.()
      blockRef.current = null
    }
  }, [hasUnsavedChanges, history])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  const closeBlocker = () => {
    blockRef.current?.()
    blockRef.current = null
  }

  const continuePendingNavigation = () => {
    const nextTransition = pendingTransitionRef.current
    pendingTransitionRef.current = null
    setDialogOpen(false)
    closeBlocker()

    if (nextTransition) {
      history.push(buildNextLocation(nextTransition))
    }
  }

  const handleSave = async () => {
    await onSave()
    continuePendingNavigation()
  }

  const handleDiscard = () => {
    onDiscard()
    continuePendingNavigation()
  }

  const handleStay = () => {
    pendingTransitionRef.current = null
    setDialogOpen(false)
  }

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      setDialogOpen(true)
      return
    }

    handleStay()
  }

  return (
    <>
      {showBar ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[950] flex justify-end px-4 pb-4">
          <div className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border border-warning/40 bg-background px-4 py-3 text-foreground shadow-2xl">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground">
                Unsaved changes
              </p>
              <p className="text-xs text-muted-foreground">
                Save before leaving this editor.
              </p>
            </div>
            <Button disabled={isSaving} onClick={onSave} size="sm">
              {isSaving ? (
                <LuLoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
            <button
              aria-label="Dismiss unsaved changes reminder"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              onClick={() => setShowBar(false)}
              type="button"
            >
              <LuX className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}

      <Dialog onOpenChange={handleDialogOpenChange} open={dialogOpen}>
        <DialogContent>
          <DialogClose />
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Save before leaving, discard them, or
              stay here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-between">
            <Button onClick={handleDiscard} variant="outline">
              Discard changes
            </Button>
            <div className="flex gap-3">
              <Button disabled={isSaving} onClick={handleSave} variant="ghost">
                {isSaving ? "Saving..." : "Save now"}
              </Button>
              <Button onClick={handleStay}>Stay</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default UnsavedChangesGuard
